import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api.js';
import { Button, Chip, Banner } from '../components/ui.jsx';
import { ghs } from '../lib/money.js';
import { exportPdfAndReveal, pdfSavesAFile, reportToCsv, downloadCsv, reportFilename } from '../lib/export.js';

const card = {
  background: 'var(--color-bg-surface)', border: '1px solid var(--color-border-default)',
  borderRadius: 'var(--radius-lg)', overflow: 'hidden'
};

const when = t => new Date(t).toLocaleString('en-GB',
  { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

/**
 * Reports the managers have sent up.
 *
 * The figures were snapshotted at the moment of sending, so opening one in March shows what
 * the manager actually sent, not what the same query would say now that prices have moved
 * and sales have been voided.
 */
export function Inbox() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(null);
  const [error, setError] = useState(null);

  const load = () => api.inbox()
    .then(d => setRows(d.deliveries))
    .catch(e => setError(e.data?.error || 'failed'));

  useEffect(() => { load(); }, []);

  async function read(id) {
    try {
      const { delivery } = await api.delivery(id);
      setOpen(delivery);
      load();                                  // the unread mark has just moved
    } catch (e) { setError(e.data?.error || 'failed'); }
  }

  if (error) return <Banner tone="danger">The inbox did not load.</Banner>;

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div className="t-body-m secondary">
        Sales reports the managers have sent up. Each is a snapshot taken when it was sent,
        so the numbers never move under you.
      </div>

      {!rows.length && (
        <div style={{ ...card, padding: 40, textAlign: 'center' }} className="t-body-m muted">
          Nothing has been sent yet. A manager sends one from Reports, in their own business.
        </div>
      )}

      {rows.map(r => (
        <button key={r.id} onClick={() => read(r.id)}
          style={{
            ...card, padding: 16, display: 'flex', gap: 14, alignItems: 'center',
            textAlign: 'left', cursor: 'pointer', font: 'inherit', color: 'inherit',
            borderLeft: r.read_at ? '1px solid var(--color-border-default)' : `3px solid ${r.business_colour || 'var(--color-accent-default)'}`
          }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="t-heading-s" style={{ fontWeight: r.read_at ? 500 : 700 }}>{r.title}</div>
            <div className="t-body-s muted">
              from {r.sent_by_name} · {when(r.sent_at)}
              {r.note ? ` · "${r.note}"` : ''}
            </div>
          </div>
          {!r.read_at && <Chip tone="accent">New</Chip>}
          {r.delivery_state === 'queued' && <Chip tone="warning">Not emailed yet</Chip>}
          <Chip showDot={false}>{r.business_name}</Chip>
          <span className="t-label-xs muted" style={{ width: 70, textAlign: 'right' }}>{r.kind.toUpperCase()}</span>
        </button>
      ))}

      {open && <DeliverySheet delivery={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

function DeliverySheet({ delivery, onClose }) {
  const ref = useRef(null);
  const r = delivery.body;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 60, display: 'grid', placeItems: 'center', padding: 24, background: 'rgba(2,6,23,0.45)' }}>
      <div className="card" style={{ width: 720, maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: 'var(--elevation-lg)' }}>
        <div ref={ref} style={{ overflowY: 'auto', padding: 24, display: 'grid', gap: 18, background: 'var(--color-bg-surface)' }}>
          <div>
            <div className="t-heading-l">{r.title}</div>
            <div className="t-body-s muted">
              Sent by {delivery.sent_by_name} on {when(delivery.sent_at)}
              {delivery.note ? <> · they wrote: "{delivery.note}"</> : null}
            </div>
          </div>

          {delivery.delivery_state === 'queued' && (
            <Banner tone="warning" title="Not emailed">{delivery.delivery_note}</Banner>
          )}

          <div className="fig-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
            <Fig label="TAKINGS" value={ghs(r.totals.revenue)} big />
            <Fig label="SALES" value={r.totals.sales} />
            <Fig label="AVERAGE SALE" value={ghs(r.totals.average)} />
            <Fig label="COMMISSION" value={ghs(r.totals.commission)} />
          </div>

          {r.totals.voided > 0 && (
            <Banner tone="info">
              {r.totals.voided} sale{r.totals.voided === 1 ? '' : 's'} worth {ghs(r.totals.voidedAmount)} were
              voided in this period. Voided sales are excluded from every figure above.
            </Banner>
          )}

          {/* One row per person per service, so the money is followed by what earned it.
              The staff totals and the service list used to sit in separate tables that never
              met, which answered how much and what, but never who did what. */}
          <Table title="WHAT EACH PERSON DID" rows={r.byStaffService || r.byStaff} cols={[
            ['staff', s => s.staff, 1],
            ['service', s => s.service || '', 1],
            ['done', s => s.qty ?? s.sold ?? s.lines, 60],
            ['takings', s => ghs(s.revenue), 120],
            ['commission', s => ghs(s.commission || 0), 120]
          ]} />

          <Table title="TOTALS PER PERSON" rows={r.byStaff} cols={[
            ['staff', s => s.staff, 1],
            ['done', s => s.done ?? s.lines, 60],
            ['takings', s => ghs(s.revenue), 120],
            ['commission', s => ghs(s.commission || 0), 120]
          ]} />

          <Table title="TOP SERVICES" rows={r.byService} cols={[
            ['service', s => s.service, 1],
            ['sold', s => s.sold, 60],
            ['takings', s => ghs(s.revenue), 120]
          ]} />

          <Table title="HOW THEY PAID" rows={r.byPayment} cols={[
            ['method', p => p.method, 1],
            ['count', p => p.count, 60],
            ['amount', p => ghs(p.amount), 120]
          ]} />
        </div>

        <div style={{ height: 1, background: 'var(--color-border-default)' }} />
        <div className="no-print" style={{ padding: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button size="m" variant="secondary"
            onClick={() => exportPdfAndReveal(ref.current, r.title)}>{pdfSavesAFile() ? 'Download as PDF' : 'Print or save as PDF'}</Button>
          <Button size="m" variant="secondary"
            onClick={() => downloadCsv(reportFilename(r), reportToCsv(r))}>Export for Excel</Button>
          <span style={{ flex: 1 }} />
          <Button size="m" onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
}

const Fig = ({ label, value, big }) => (
  <div style={{ padding: 14, background: 'var(--color-bg-subtle)', borderRadius: 'var(--radius-md)' }}>
    <div className="t-label-xs muted">{label}</div>
    <div className={big ? 'money t-money-l' : 'money t-money-m'} style={{ marginTop: 2 }}>{value}</div>
  </div>
);

function Table({ title, rows, cols }) {
  if (!rows?.length) return null;
  return (
    <div>
      <div className="t-label-xs muted" style={{ marginBottom: 6 }}>{title}</div>
      {/* Scrolls inside itself on a narrow screen rather than pushing the page sideways. A
          page that scrolls sideways loses its own navigation off the edge, and a report with
          five columns is wider than a phone whatever is done to it. */}
      <div className="scroll-x" style={{ border: '1px solid var(--color-border-default)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
        {rows.map((row, i) => (
          <div key={i} style={{
            display: 'flex', gap: 12, padding: '9px 12px',
            borderTop: i ? '1px solid var(--color-border-default)' : 0
          }}>
            {cols.map(([, get, width], j) => (
              <span key={j}
                className={j === 0 ? 't-body-m' : 'money t-money-s'}
                style={width === 1
                  ? { flex: 1, minWidth: 0, textTransform: j === 0 ? 'capitalize' : 'none' }
                  : { width, flex: 'none', textAlign: 'right' }}>
                {get(row)}
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
